// src/utils/mediaUrl.js

/**
 * Gets the base URL for media files
 * @returns {string} - The base URL for media
 */
export const getMediaBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://virtualmedic.uk';
  }
  return 'http://localhost:3000'; // For development, assuming nginx is proxying
};

const normalizeMediaRelativePath = (value = '') => value
  .toString()
  .trim()
  .replace(/\\/g, '/')
  .replace(/^\/+/, '')
  .replace(/^(?:uploads|media)\//, '');

/**
 * Constructs a full media URL from a relative path with enhanced fallback handling
 * @param {string|object} file - The file path or file object
 * @param {string} fallback - Fallback URL if file is invalid
 * @returns {string|null} - The full media URL or fallback
 */
export const getMediaUrl = (file, fallback = null) => {
  try {
    if (!file) return fallback;
    
    // Handle different file object structures
    if (typeof file === 'string') {
      const relativePath = file;
      if (/^https?:\/\//i.test(relativePath)) return relativePath;
      const cleanPath = normalizeMediaRelativePath(relativePath);
      return `${getMediaBaseUrl()}/media/${cleanPath}`;
    }
    
    if (file.url) {
      if (/^https?:\/\//i.test(file.url)) return file.url;
      return `${getMediaBaseUrl()}/media/${normalizeMediaRelativePath(file.url)}`;
    }
    if (file.path) {
      if (/^https?:\/\//i.test(file.path)) return file.path;
      return `${getMediaBaseUrl()}/media/${normalizeMediaRelativePath(file.path)}`;
    }
    
    return fallback;
  } catch (error) {
    console.error('Error generating media URL:', error);
    return fallback;
  }
};

/**
 * Constructs a character profile picture URL
 * @param {string} profilePicture - The profile picture path
 * @returns {string|null} - The full URL or null
 */
export const getCharacterImageUrl = (profilePicture) => {
  return getMediaUrl(profilePicture);
};

/**
 * Constructs a user profile picture URL
 * @param {string} profilePicture - The profile picture path
 * @returns {string|null} - The full URL or null
 */
export const getUserProfileUrl = (profilePicture) => {
  return getMediaUrl(profilePicture);
};

/**
 * Constructs a question media file URL
 * @param {string} mediaPath - The media file path
 * @returns {string|null} - The full URL or null
 */
export const getQuestionMediaUrl = (mediaPath) => {
  return getMediaUrl(mediaPath);
};

/**
 * Validates a media file for upload
 * @param {File} file - The file to validate
 * @returns {boolean} - True if valid, throws error if invalid
 */
export const validateMediaFile = (file) => {
  const maxSize = 50 * 1024 * 1024; // 50MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  
  if (file.size > maxSize) {
    throw new Error('File size exceeds 50MB limit');
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only images are allowed.');
  }
  
  return true;
};
